import React, { HTMLAttributes, useCallback, useRef, useEffect, useState } from 'react';

import {
  AUDIO_MIMETYPE,
  IMAGE_MIMETYPE,
  PDF_MIMETYPE,
  TEXT_MIMETYPE,
  VIDEO_MIMETYPE,
} from 'helpers/FileHelpers';
import {
  isHeicFile,
  isTiffFile,
  convertHeicFileToJpeg,
  convertTiffFileToJpeg,
} from 'helpers/heicConverter';
import TrashIcon from './TrashIcon';
import {
  FilePreviewContainer,
  ImagePreview,
  RemoveFileContainer,
  VideoPreview,
} from '../components';
import PdfPreview from '../../../pdf-preview';
import TxtPreview from './TxtPreview';
import { useSelector } from 'react-redux';
import { selectIsCelebrateModalOpen } from 'modules/modal/selectors';
import { UploadFile } from '../index';
import localization from 'localization';

type FilePreviewProps = HTMLAttributes<HTMLDivElement> & {
  file?: { uri: string; type: string; name: string };
  removeFile?: () => void;
  disabled?: boolean;
};

const FilePreview = ({
  file,
  removeFile = () => null,
  disabled,
}: FilePreviewProps) => {
  const vidRef = useRef<HTMLVideoElement>(null);
  const [convertedFile, setConvertedFile] = useState<UploadFile | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isCelebrateModalOpen = useSelector(selectIsCelebrateModalOpen);

  // Convert HEIC and TIFF to JPEG when file is loaded
  useEffect(() => {
    if (!file || file.type !== IMAGE_MIMETYPE) {
      setConvertedFile(null);
      setIsConverting(false);
      setImageError(false);
      return;
    }

    const fileAsUploadFile = file as UploadFile;

    // Only attempt conversion if we have strong indication it's HEIC/TIFF
    // Don't convert if file has a clear extension that's not HEIC/TIFF (e.g., .jpg, .png)
    const hasNonHeicTiffExtension = file.name &&
      !file.name.toLowerCase().match(/\.(heic|heif|tif|tiff)$/i) &&
      file.name.includes('.');

    // Check if it's a HEIC file and needs conversion (detected from metadata)
    if (isHeicFile(fileAsUploadFile) && !hasNonHeicTiffExtension) {
      setIsConverting(true);
      setImageError(false);
      convertHeicFileToJpeg(fileAsUploadFile)
        .then((converted) => {
          if (converted) {
            setConvertedFile(converted);
          } else {
            // If conversion fails, use original (Safari might support it)
            setConvertedFile(null);
          }
          setIsConverting(false);
        })
        .catch((error) => {
          console.error('Error converting HEIC:', error);
          setConvertedFile(null);
          setIsConverting(false);
        });
      return;
    }
    // Check if it's a TIFF file and needs conversion (detected from metadata)
    if (isTiffFile(fileAsUploadFile) && !hasNonHeicTiffExtension) {
      setIsConverting(true);
      setImageError(false);
      convertTiffFileToJpeg(fileAsUploadFile)
        .then((converted) => {
          if (converted) {
            setConvertedFile(converted);
          } else {
            // If conversion fails, mark as error
            setConvertedFile(null);
            setImageError(true);
          }
          setIsConverting(false);
        })
        .catch((error) => {
          console.error('Error converting TIFF:', error);
          setConvertedFile(null);
          setImageError(true);
          setIsConverting(false);
        });
      return;
    }

    // For files without detectable metadata (e.g., from S3 without extension),
    // we'll try to render the image normally and let handleImageError attempt conversion
    // ONLY if the image fails to load. This avoids unnecessary conversion attempts.
    setConvertedFile(null);
    setIsConverting(false);
    setImageError(false);
  }, [file]);

  // Handler for when image fails to load - try conversion as fallback
  // Only attempt conversion if image actually failed to load
  const handleImageError = useCallback(async () => {
    if (!file || file.type !== IMAGE_MIMETYPE || imageError || isConverting) {
      return;
    }

    // Only try conversion if we don't already have a converted file
    // and the image actually failed to load (not just a warning)
    if (convertedFile) {
      return;
    }

    const fileAsUploadFile = file as UploadFile;

    // Always attempt conversion with force: true when image fails to load
    // This handles cases where TIFF/HEIC files come from S3 without extension
    // and with generic MIME type (image/*). The conversion functions will validate
    // internally if the file is actually a valid HEIC/TIFF when force is true.
    setIsConverting(true);
    setImageError(false);

    // Try converting as TIFF first (more common than HEIC)
    // with force to attempt even if not detected
    try {
      const tiffConverted = await convertTiffFileToJpeg(fileAsUploadFile, true);
      if (tiffConverted) {
        setConvertedFile(tiffConverted);
        setIsConverting(false);
        setImageError(false);
        return;
      }
    } catch (error) {
      // Silently continue to try HEIC
    }

    // Try converting as HEIC (with force to attempt even if not detected)
    try {
      const heicConverted = await convertHeicFileToJpeg(fileAsUploadFile, true);
      if (heicConverted) {
        setConvertedFile(heicConverted);
        setIsConverting(false);
        setImageError(false);
        return;
      }
    } catch (error) {
      // Conversion failed
    }

    // If both conversions fail, mark as error
    setIsConverting(false);
    setImageError(true);
  }, [file, convertedFile, imageError, isConverting]);

  const renderPreview = useCallback(() => {
    if (!file) {
      return null;
    }

    if (file.type === IMAGE_MIMETYPE) {
      // Use converted file if available, otherwise use original
      const imageSrc = convertedFile?.uri || file.uri;

      // Show loading state while converting
      if (isConverting) {
        return (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>{localization.convertingHeicImage || 'Loading content...'}</p>
          </div>
        );
      }

      // Show error message if image failed to load and conversion also failed
      if (imageError && !convertedFile) {
        return (
          <div style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
            <p>{localization.imageCannotBeDisplayed || 'The image cannot be displayed.'}</p>
          </div>
        );
      }

      return (
        <ImagePreview
          src={imageSrc}
          onError={(e) => {
            // Only attempt conversion if the image actually failed to load
            // and we're using the original source (not a converted one)
            if (!convertedFile && imageSrc === file.uri) {
              handleImageError();
            }
          }}
          data-testid="preview-drag-drop-zone"
        />
      );
    }

    if (file.type === PDF_MIMETYPE) {
      return <PdfPreview file={file} data-testid="preview-drag-drop-zone" />;
    }

    if (file.type === AUDIO_MIMETYPE) {
      return (
        <audio src={file.uri} controls data-testid="preview-drag-drop-zone" />
      );
    }

    if (file.type === VIDEO_MIMETYPE) {
      return (
        <VideoPreview
          ref={vidRef}
          src={file.uri}
          controls
          autoPlay
          data-testid="preview-drag-drop-zone"
        />
      );
    }

    if (file.type === TEXT_MIMETYPE) {
      return <TxtPreview file={file} data-testid="preview-drag-drop-zone" />;
    }

    return <div>This file type is not supported yet.</div>;
  }, [file, convertedFile, isConverting, imageError, handleImageError]);

  useEffect(() => {
    isCelebrateModalOpen && vidRef.current && vidRef.current.pause();
  }, [isCelebrateModalOpen, vidRef]);

  return file && file.type && file.uri ? (
    <FilePreviewContainer data-testid="preview-drag-drop-zone">
      {renderPreview()}
      {disabled ? null : (
        <RemoveFileContainer onClick={removeFile}>
          <TrashIcon />
        </RemoveFileContainer>
      )}
    </FilePreviewContainer>
  ) : null;
};

export default FilePreview;
